import { CommonModule } from "@angular/common";
import { Component, DestroyRef, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, of } from "rxjs";

import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PHMS_SUBJECT_TERMINOLOGY } from "../phms-subject.terminology";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "../phms-operational.terminology";
import { personFirstNames, personLastName } from "@app/shared/util/person-name";
import { PhmsIntakeRuntimeService } from "../intake/phms-intake-runtime.service";
import {
    PhmsIntakeSubmissionDto,
    PhmsIntakeDisplayError,
    mapPhmsIntakeError,
} from "../intake/phms-manual-intake-submission.models";
import { PhmsManualIntakeSubmissionService } from "../intake/phms-manual-intake-submission.service";

const PHMS_INTAKE_SUBMIT = "phms.intake.submit";

@Component({
    selector: "phms-intake-submission-list",
    standalone: true,
    imports: [CommonModule, UiLibraryModule, RouterLink],
    templateUrl: "./phms-intake-submission-list.component.html",
    styleUrl: "./phms-intake-submission-list.component.scss",
})
export class PhmsIntakeSubmissionListComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly roles = inject(RoleService);
    private readonly intakeService = inject(PhmsManualIntakeSubmissionService);
    private readonly runtime = inject(PhmsIntakeRuntimeService);
    readonly subjectText = createTerminologyUsageSignals(PHMS_SUBJECT_TERMINOLOGY.intakeList);
    readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.intakeList);

    readonly canRead = this.roles.hasAnyPermissionSignal(PHMS_INTAKE_SUBMIT);

    readonly isLoading = signal(true);
    readonly intakeSubmissions = signal<PhmsIntakeSubmissionDto[]>([]);
    readonly errorState = signal<PhmsIntakeDisplayError | null>(null);
    readonly totalCount = signal(0);

    readonly hasIntakeSubmissions = computed(() => this.intakeSubmissions().length > 0);

    readonly identifierLabel = this.runtime.incarceratedPersonIdentifierLabel;
    readonly programScopeKeyLabel = this.runtime.programScopeKeyLabel;

    readonly pageTitle = "PHMS Intake Submissions";
    readonly pageSubtitle =
        "Most recent PHMS intake submissions. Select a row to open the detail view.";

    constructor() {
        this.runtime.loadFieldLabels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.refresh();
    }

    /** Submission name split into the table's Last Name / First Name columns; raw name stays on the row. */
    lastNameOf(intakeSubmission: PhmsIntakeSubmissionDto): string {
        return personLastName(intakeSubmission.incarceratedPersonDisplayName);
    }

    firstNamesOf(intakeSubmission: PhmsIntakeSubmissionDto): string {
        return personFirstNames(intakeSubmission.incarceratedPersonDisplayName);
    }

    refresh(): void {
        this.errorState.set(null);
        this.isLoading.set(true);

        this.intakeService
            .list({ pageNumber: 1, pageSize: 50 })
            .pipe(
                catchError((error: unknown) => {
                    this.errorState.set(mapPhmsIntakeError(error));
                    return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((result) => {
                if (result) {
                    this.intakeSubmissions.set(result.items);
                    this.totalCount.set(result.totalCount);
                } else {
                    this.intakeSubmissions.set([]);
                    this.totalCount.set(0);
                }
                this.isLoading.set(false);
            });
    }
}

